import { useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../src/hooks/useAuth';
import { userAuthApi } from '../features/auth/services';
import type { SocialAuthSuccessResponse } from '../features/auth/services/userAuthApi';
import type { RegistrationConsents } from '../features/legal-consent/types';
import { getAndClearReturnUrl } from '../lib/api/auth/utils';
import {
  renderGoogleButton,
  showOneTapPrompt,
  getGoogleCsrfToken,
  GSI_EMPTY_CREDENTIAL_ERROR,
} from '../lib/social/GoogleSignIn';
import { signInWithApple } from '../lib/social/AppleSignIn';

/**
 * Social provider types - Google and Apple only (Facebook removed)
 */
export type SocialProvider = 'google' | 'apple';

export interface SocialLoginResult {
  success: boolean;
  user?: {
    id: string;
    email: string;
    name: string;
    provider: SocialProvider;
    avatar?: string;
  };
  tokens?: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    tokenType: string;
  };
  error?: string;
}

export interface SocialLoginState {
  isLoading: boolean;
  error: string | null;
  provider: SocialProvider | null;
}

/** Stored OAuth credentials waiting for user consent before calling the API */
export interface PendingSocialAuth {
  provider: SocialProvider;
  idToken: string;
  gCsrfToken?: string;
  /** Raw nonce for replay protection — both Google and Apple (backend verifies against token claim) */
  nonce?: string;
}

/** Error code for missing Google client ID - translate in SocialLogin */
export const GOOGLE_NOT_CONFIGURED = 'GOOGLE_NOT_CONFIGURED';

/** Extract HTTP status from a thrown API error (apiClient wraps errors with response.status) */
function getErrorHttpStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    const res = (error as { response?: { status?: number } }).response;
    return res?.status;
  }
  return undefined;
}

/** Delay helper for retry backoff */
const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Check if an API error or response indicates consent is required (new user) */
function isConsentRequired(errorOrResponse: unknown): boolean {
  // Thrown error with response data (axios-style)
  if (errorOrResponse && typeof errorOrResponse === 'object') {
    const err = errorOrResponse as Record<string, unknown>;
    // Direct error code on response
    if (err.error === 'CONSENT_REQUIRED') return true;
    // Nested response (thrown from apiClient)
    const res = err.response as { data?: { error?: string } } | undefined;
    if (res?.data?.error === 'CONSENT_REQUIRED') return true;
    // Error message fallback
    if (err.message && typeof err.message === 'string' && err.message.includes('Consent is required')) return true;
  }
  return false;
}

/**
 * Map API error to user-friendly message
 */
function getSocialLoginErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const res = (error as { response?: { status?: number; data?: { message?: string; error?: string } } })
      .response;
    if (res?.status === 400) {
      return (res.data?.message || res.data?.error) ?? 'Invalid request. Please try again.';
    }
    if (res?.status === 401) {
      return 'Sign-in failed. Please try again.';
    }
    if (res?.status === 403) {
      return 'Your account has been suspended. Contact support.';
    }
  }
  if (error instanceof Error) {
    if (error.message.includes('Network') || error.message.includes('fetch')) {
      return 'Connection error. Please check your connection and try again.';
    }
    return error.message;
  }
  return 'Sign-in failed. Please try again.';
}

export interface UseSocialLoginOptions {
  /** Called when social login succeeds (e.g. for overlay mode to close) */
  onSuccess?: (user: { id: string; email: string; name: string; provider: SocialProvider }) => void;
  /** When provided, skip navigate and call this instead (overlay mode) */
  onAuthenticated?: () => void;
}

export interface UseSocialLoginReturn {
  isLoading: boolean;
  error: string | null;
  currentProvider: SocialProvider | null;
  /** OAuth credentials pending consent confirmation (null = no pending auth) */
  pendingSocialAuth: PendingSocialAuth | null;
  handleSocialLogin: (
    provider: SocialProvider,
    credential?: string,
    gCsrfToken?: string
  ) => Promise<SocialLoginResult>;
  handleGoogleLogin: (credential: string, gCsrfToken?: string, nonce?: string) => Promise<SocialLoginResult>;
  handleAppleLogin: () => Promise<SocialLoginResult>;
  /** Complete pending social login with GDPR consent flags */
  completeSocialAuth: (consents: RegistrationConsents) => Promise<SocialLoginResult>;
  /** Cancel pending social login and discard stored credentials */
  cancelSocialAuth: () => void;
  renderGoogleSignInButton: (container: HTMLElement) => void;
  /** Trigger Google One Tap prompt (floating account picker for unauthenticated users) */
  triggerGoogleOneTap: () => void;
  clearError: () => void;
}

/**
 * useSocialLogin - Production-grade social login hook
 * Handles Google (GSI) and Apple Sign-In with ID token flow
 * Calls POST /api/v1/auth/social and completes auth via AuthContext
 */
export const useSocialLogin = (options?: UseSocialLoginOptions): UseSocialLoginReturn => {
  const { onSuccess, onAuthenticated } = options ?? {};
  const onSuccessRef = useRef(onSuccess);
  const onAuthenticatedRef = useRef(onAuthenticated);
  onSuccessRef.current = onSuccess;
  onAuthenticatedRef.current = onAuthenticated;

  const [state, setState] = useState<SocialLoginState>({
    isLoading: false,
    error: null,
    provider: null,
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Hydrate pending auth from route state (set by GoogleOneTap when CONSENT_REQUIRED)
  const initialPending = (location.state as { pendingSocialAuth?: PendingSocialAuth } | null)
    ?.pendingSocialAuth ?? null;
  const [pendingSocialAuth, setPendingSocialAuth] = useState<PendingSocialAuth | null>(initialPending);
  const { completeAuthentication } = useAuth();
  const completeAuthRef = useRef(completeAuthentication);
  completeAuthRef.current = completeAuthentication;

  /**
   * Perform redirect after successful login (mirrors handleLoginSuccess from MultiMethodLoginForm)
   * When onAuthenticated is provided (overlay mode), call it and skip navigate
   */
  const performPostLoginRedirect = useCallback(() => {
    const onAuth = onAuthenticatedRef.current;
    if (onAuth) {
      onAuth();
      return;
    }

    const storedReturnUrl = getAndClearReturnUrl();
    const stateReturnUrl = (location.state as { from?: string } | null)?.from;
    const returnToChat = sessionStorage.getItem('amalq_return_to_chat') === 'true';
    const chatReturnUrl = sessionStorage.getItem('amalq_chat_return_url') || '/';
    const returnUrl =
      storedReturnUrl ||
      stateReturnUrl ||
      (returnToChat ? chatReturnUrl : '/dashboard');

    if (returnToChat) {
      sessionStorage.removeItem('amalq_return_to_chat');
      sessionStorage.removeItem('amalq_chat_return_url');
    }

    // Toast is shown by the form component (MultiMethodLoginForm/LoginForm)
    // to avoid duplicate toasts. The hook handles redirect only.
    setTimeout(() => {
      navigate(returnUrl, { replace: true });
      if (returnToChat) {
        sessionStorage.setItem('amalq_reopen_chat', 'true');
      }
    }, 1500);
  }, [location.state, navigate]);

  /**
   * Internal: complete auth from a successful API response.
   * Shared by optimistic login and consent-retry paths.
   */
  const completeFromResponse = useCallback(
    (provider: SocialProvider, response: SocialAuthSuccessResponse): SocialLoginResult => {
      const { user, tokens } = response.data;
      void completeAuthRef.current({ user, tokens });
      const resultUser = {
        id: user.id,
        email: user.email,
        name: user.name ?? user.email,
        provider,
        avatar: user.avatar,
      };
      setState({ isLoading: false, error: null, provider: null });
      setPendingSocialAuth(null);
      onSuccessRef.current?.(resultUser);
      performPostLoginRedirect();
      return { success: true, user: resultUser, tokens };
    },
    [performPostLoginRedirect]
  );

  /**
   * Internal: call social login API with optional consents.
   * Returns the result or throws on network/unexpected errors.
   */
  const callSocialApi = useCallback(
    async (
      provider: SocialProvider,
      idToken: string,
      gCsrfToken?: string,
      nonce?: string,
      consents?: RegistrationConsents
    ) => {
      return userAuthApi.socialLogin({
        provider,
        idToken,
        g_csrf_token: provider === 'google' ? (gCsrfToken ?? getGoogleCsrfToken()) : undefined,
        nonce,
        consents,
      });
    },
    []
  );

  /**
   * Optimistic social login: try API without consents first.
   * - Existing user → logged in instantly (no consent screen)
   * - New user → backend returns CONSENT_REQUIRED → show consent step
   * Used by both Google (button + One Tap) and Apple flows.
   *
   * Auto-retry: on HTTP 400 (first attempt only), waits 1.5s and retries once.
   * Production data shows transient 400s from empty idToken race conditions
   * consistently succeed on retry within 3-6 seconds.
   */
  const attemptSocialLogin = useCallback(
    async (
      provider: SocialProvider,
      idToken: string,
      gCsrfToken?: string,
      nonce?: string,
      isRetry = false
    ): Promise<SocialLoginResult> => {
      if (!isRetry) {
        setState({ isLoading: true, error: null, provider });
      }

      try {
        const response = await callSocialApi(provider, idToken, gCsrfToken, nonce);

        if (response.success && response.data) {
          return completeFromResponse(provider, response as SocialAuthSuccessResponse);
        }

        // Check if consent is required (new user)
        if (isConsentRequired(response)) {
          setPendingSocialAuth({ provider, idToken, gCsrfToken, nonce });
          setState({ isLoading: false, error: null, provider: null });
          return { success: false }; // Will show consent UI
        }

        const errMsg =
          (response as { message?: string; error?: string }).message ??
          (response as { message?: string; error?: string }).error ??
          'Sign-in failed. Please try again.';
        setState({ isLoading: false, error: errMsg, provider: null });
        return { success: false, error: errMsg };
      } catch (error) {
        // CONSENT_REQUIRED may come as a thrown error (400)
        if (isConsentRequired(error)) {
          setPendingSocialAuth({ provider, idToken, gCsrfToken, nonce });
          setState({ isLoading: false, error: null, provider: null });
          return { success: false }; // Will show consent UI
        }

        // Auto-retry once on HTTP 400 (transient GSI token race condition)
        const status = getErrorHttpStatus(error);
        if (status === 400 && !isRetry) {
          await delay(1500);
          return attemptSocialLogin(provider, idToken, gCsrfToken, nonce, true);
        }

        const errMsg = getSocialLoginErrorMessage(error);
        setState({ isLoading: false, error: errMsg, provider: null });
        return { success: false, error: errMsg };
      }
    },
    [callSocialApi, completeFromResponse]
  );

  /**
   * Handle Google login: try API first, show consent only for new users.
   * Called by GSI button callback and One Tap prompt.
   * Layer 2 defense: validates credential is non-empty before calling the API.
   */
  const handleGoogleLogin = useCallback(
    async (credential: string, gCsrfToken?: string, nonce?: string): Promise<SocialLoginResult> => {
      if (!credential || typeof credential !== 'string' || credential.trim().length === 0) {
        const errMsg = GSI_EMPTY_CREDENTIAL_ERROR;
        setState({ isLoading: false, error: errMsg, provider: null });
        return { success: false, error: errMsg };
      }
      return attemptSocialLogin('google', credential, gCsrfToken, nonce);
    },
    [attemptSocialLogin]
  );

  /**
   * Handle Apple login: trigger popup, then try API first.
   */
  const handleAppleLogin = useCallback(async (): Promise<SocialLoginResult> => {
    setState({ isLoading: true, error: null, provider: 'apple' });

    try {
      const { idToken, nonce } = await signInWithApple();
      return attemptSocialLogin('apple', idToken, undefined, nonce);
    } catch (error) {
      // User cancel — do not show error
      const err = error as Error & { error?: string };
      if (err?.error === 'popup_closed_by_user' || err?.message?.includes('cancel')) {
        setState({ isLoading: false, error: null, provider: null });
        return { success: false };
      }
      const errMsg = getSocialLoginErrorMessage(error);
      setState({ isLoading: false, error: errMsg, provider: null });
      return { success: false, error: errMsg };
    }
  }, [attemptSocialLogin]);

  /**
   * Complete pending social login with GDPR consent flags.
   * Called after user accepts ToS + Privacy Policy in the consent dialog.
   * This is the retry path after CONSENT_REQUIRED — sends consents with the same credentials.
   */
  const completeSocialAuth = useCallback(
    async (consents: RegistrationConsents): Promise<SocialLoginResult> => {
      if (!pendingSocialAuth) {
        return { success: false, error: 'No pending authentication' };
      }
      const { provider, idToken, gCsrfToken, nonce } = pendingSocialAuth;
      setState({ isLoading: true, error: null, provider });

      try {
        const response = await callSocialApi(provider, idToken, gCsrfToken, nonce, consents);

        if (response.success && response.data) {
          return completeFromResponse(provider, response as SocialAuthSuccessResponse);
        }

        const errMsg =
          (response as { message?: string; error?: string }).message ??
          (response as { message?: string; error?: string }).error ??
          'Sign-in failed. Please try again.';
        setState({ isLoading: false, error: errMsg, provider: null });
        return { success: false, error: errMsg };
      } catch (error) {
        const errMsg = getSocialLoginErrorMessage(error);
        setState({ isLoading: false, error: errMsg, provider: null });
        return { success: false, error: errMsg };
      }
    },
    [pendingSocialAuth, callSocialApi, completeFromResponse]
  );

  /**
   * Cancel pending social login and discard stored credentials.
   */
  const cancelSocialAuth = useCallback(() => {
    setPendingSocialAuth(null);
    setState({ isLoading: false, error: null, provider: null });
  }, []);

  /**
   * Unified handler for SocialLogin component
   */
  const handleSocialLogin = useCallback(
    async (provider: SocialProvider, credential?: string, gCsrfToken?: string): Promise<SocialLoginResult> => {
      if (provider === 'google' && credential) {
        return handleGoogleLogin(credential, gCsrfToken);
      }
      if (provider === 'apple') {
        return handleAppleLogin();
      }
      return { success: false, error: 'Invalid provider' };
    },
    [handleGoogleLogin, handleAppleLogin]
  );

  /**
   * Trigger Google One Tap prompt — the floating account picker.
   * Shows automatically for users with a Google session. Google handles dismiss cooldown.
   * Uses the same optimistic flow: existing users log in instantly, new users see consent.
   */
  const triggerGoogleOneTap = useCallback(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return;
    void showOneTapPrompt(
      clientId,
      (cred, gCsrf, nonce) => {
        void handleGoogleLogin(cred, gCsrf, nonce);
      },
      (errorMsg) => {
        setState({ isLoading: false, error: errorMsg, provider: null });
      }
    );
  }, [handleGoogleLogin]);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  /**
   * Render Google button into container (for SocialLogin component)
   */
  const renderGoogleSignInButton = useCallback(
    (container: HTMLElement) => {
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!clientId) {
        setState((prev) => ({ ...prev, error: GOOGLE_NOT_CONFIGURED }));
        return;
      }
      renderGoogleButton(
        container,
        clientId,
        (cred, gCsrf, nonce) => {
          void handleGoogleLogin(cred, gCsrf, nonce);
        },
        (errorMsg) => {
          setState({ isLoading: false, error: errorMsg, provider: null });
        }
      );
    },
    [handleGoogleLogin]
  );

  return {
    isLoading: state.isLoading,
    error: state.error,
    currentProvider: state.provider,
    pendingSocialAuth,
    handleSocialLogin,
    handleGoogleLogin,
    handleAppleLogin,
    completeSocialAuth,
    cancelSocialAuth,
    renderGoogleSignInButton,
    triggerGoogleOneTap,
    clearError,
  };
};
